import type { VirtualClinicAnalysisOperatingNumberResponsePart } from '../../resolving/response';
import type { VirtualClinicAnalysisOperatingNumberGroupBy } from '../group-by/group-by';
import { VirtualClinicAnalysisOperatingNumberGroupBySortHelper } from '../group-by/sort-helper';
import type { VirtualClinicAnalysisOperatingNumberResultNode } from './result-node';
import type {
  VirtualClinicAnalysisOperatingNumberLeafItem,
  VirtualClinicAnalysisOperatingNumberNodeItem,
} from '../../resolving/operating-number-item';
import type { VirtualClinicAnalysisOperatingNumberResultSetOperation } from '../result-set-operation';

export class VirtualClinicAnalysisOperatingNumberResultHelper {
  public static createOperatingNumberDataNodes(
    parts: { id: string }[],
    responses: {
      [chartPartId: string]: VirtualClinicAnalysisOperatingNumberResponsePart;
    },
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[],
    resultSetOperation?: VirtualClinicAnalysisOperatingNumberResultSetOperation,
  ): {
    dictionary: {
      [label: string]: { shortDisplayName: string; longDisplayName: string };
    };
    aggregatedRoots: { [partId: string]: number };
    nodes: VirtualClinicAnalysisOperatingNumberResultNode[];
  } {
    const result = { aggregatedRoots: {}, nodes: [], dictionary: {} };

    let operatingNumberData = [];

    for (const part of parts) {
      const response = responses[part.id];
      if (response) {
        result.aggregatedRoots[part.id] = response.aggregatedValue;

        const partOperatingNumberData =
          this._groupedValuesToOperatingNumberDataNodes(
            part.id,
            response.groupedValues,
          );

        operatingNumberData = this._mergeOperatingNumberDataNode(
          part.id,
          partOperatingNumberData,
          operatingNumberData,
        );
      }
    }

    result.nodes = this._sortOperatingNumberDataNodes(
      parts.map((chartPart) => chartPart.id),
      operatingNumberData,
      groupBy,
      resultSetOperation,
    );

    result.dictionary = this._getDataNodeLabels(result.nodes);

    return result;
  }

  protected static _getDataNodeLabels(
    nodes: VirtualClinicAnalysisOperatingNumberResultNode[],
    dictionary: {
      [key: string]: { shortDisplayName: string; longDisplayName: string };
    } = {},
  ): { [key: string]: { shortDisplayName: string; longDisplayName: string } } {
    nodes.forEach((node) => {
      if (node.children) {
        dictionary = this._getDataNodeLabels(node.children, dictionary);
      }

      if (!dictionary[node.label.shortDisplayName]) {
        dictionary[node.label.shortDisplayName] = {
          shortDisplayName: node.label.shortDisplayName,
          longDisplayName: node.label.longDisplayName,
        };
      }
    });

    return dictionary;
  }

  protected static _sortOperatingNumberDataNodes(
    chartPartIds: string[],
    operatingNumberDataNodes: VirtualClinicAnalysisOperatingNumberResultNode[],
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[],
    resultSetOperation?: VirtualClinicAnalysisOperatingNumberResultSetOperation,
    depth = 0,
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    let sortedOperatingNumberDataNodes = operatingNumberDataNodes;
    const currentDepthGroupBy = groupBy[depth];
    const reverse = currentDepthGroupBy.sort?.direction === 'DESC' ? -1 : 1;

    if (resultSetOperation) {
      sortedOperatingNumberDataNodes =
        this._sortOperatingNumberDataNodesByResultSetOperation(
          resultSetOperation,
          operatingNumberDataNodes,
        );
    } else if (
      !currentDepthGroupBy.sort ||
      currentDepthGroupBy.sort?.sortBy === 'LABEL'
    ) {
      sortedOperatingNumberDataNodes =
        this._sortOperatingNumberDataNodesByLabel(
          currentDepthGroupBy,
          operatingNumberDataNodes,
          reverse,
        );
    } else if (currentDepthGroupBy.sort?.sortBy === 'VALUE') {
      sortedOperatingNumberDataNodes =
        this._sortOperatingNumberDataNodesByValue(
          chartPartIds,
          operatingNumberDataNodes,
          reverse,
        );
    }

    for (const operatingNumberDataNode of sortedOperatingNumberDataNodes) {
      if (operatingNumberDataNode.children) {
        operatingNumberDataNode.children = this._sortOperatingNumberDataNodes(
          chartPartIds,
          operatingNumberDataNode.children,
          groupBy,
          resultSetOperation,
          depth + 1,
        );
      }
    }

    return sortedOperatingNumberDataNodes;
  }

  protected static _sortOperatingNumberDataNodesByResultSetOperation(
    resultSetOperation: VirtualClinicAnalysisOperatingNumberResultSetOperation,
    operatingNumberDataNodes: VirtualClinicAnalysisOperatingNumberResultNode[],
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    const sortFnc = (
      a: VirtualClinicAnalysisOperatingNumberResultNode,
      b: VirtualClinicAnalysisOperatingNumberResultNode,
    ) => {
      const sorter = {
        'Unteres Quartil': 0,
        Median: 1,
        'Oberes Quartil': 2,
      };

      return (
        sorter[a.label.shortDisplayName] - sorter[b.label.shortDisplayName]
      );
    };

    return operatingNumberDataNodes.sort(sortFnc);
  }

  protected static _sortOperatingNumberDataNodesByLabel(
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy,
    operatingNumberDataNodes: VirtualClinicAnalysisOperatingNumberResultNode[],
    reverse: -1 | 1,
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    const sortFnc =
      VirtualClinicAnalysisOperatingNumberGroupBySortHelper.getSortByGroupLabelFnc(
        groupBy,
        reverse,
      );

    return operatingNumberDataNodes.sort(sortFnc);
  }

  protected static _sortOperatingNumberDataNodesByValue(
    chartPartIds: string[],
    operatingNumberDataNodes: VirtualClinicAnalysisOperatingNumberResultNode[],
    reverse: -1 | 1,
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    const sorter = (
      data: VirtualClinicAnalysisOperatingNumberResultNode[],
      sort,
    ) => {
      const test = (a, b) =>
        Math.sign(a - b) ? reverse * Math.sign(a - b) : 0;
      return data.sort(
        (a, b) =>
          sort
            .map((s) => test(a.values[s], b.values[s]))
            .filter((item) => item !== 0)[0],
      );
    };

    return sorter(operatingNumberDataNodes, chartPartIds);
  }

  protected static _groupedValuesToOperatingNumberDataNodes(
    id: string,
    groupedValues: (
      | VirtualClinicAnalysisOperatingNumberNodeItem
      | VirtualClinicAnalysisOperatingNumberLeafItem
    )[],
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    return (groupedValues ?? []).map((groupedValue) => {
      const value =
        groupedValue.type === 'node'
          ? (groupedValue as VirtualClinicAnalysisOperatingNumberNodeItem)
              .aggregated
          : (groupedValue as VirtualClinicAnalysisOperatingNumberLeafItem)
              .value;
      const operatingNumberDataNode: VirtualClinicAnalysisOperatingNumberResultNode =
        {
          label: groupedValue.label,
          values: { [id]: value },
        };

      if (groupedValue.type === 'node') {
        operatingNumberDataNode.children =
          this._groupedValuesToOperatingNumberDataNodes(
            id,
            (groupedValue as VirtualClinicAnalysisOperatingNumberNodeItem)
              .groupedValues as (
              | VirtualClinicAnalysisOperatingNumberNodeItem
              | VirtualClinicAnalysisOperatingNumberLeafItem
            )[],
          );
      }
      return operatingNumberDataNode;
    });
  }

  /**
   * Merge two arrays of operating number nodes. Each node of the source array will be merged into the target array.
   * When the source node already exists in the target array, the children will be merged.
   * When the source node does not exist in the target array, it will be added.
   *
   * @param srcId - The id of the chart part the source nodes belong to.
   * @param src - The node to merge.
   * @param target - The nodes to merge into.
   * @protected
   */
  protected static _mergeOperatingNumberDataNode(
    srcId: string,
    src: VirtualClinicAnalysisOperatingNumberResultNode[],
    target: VirtualClinicAnalysisOperatingNumberResultNode[] = [],
  ): VirtualClinicAnalysisOperatingNumberResultNode[] {
    for (const srcNode of src) {
      const existingIndex = target.findIndex(
        (it) => it.label.key === srcNode.label.key,
      );

      if (existingIndex === -1) {
        target.push(srcNode);
      } else if (existingIndex > -1) {
        target[existingIndex].values = {
          ...target[existingIndex].values,
          ...srcNode.values,
        };

        if (srcNode.children) {
          target[existingIndex].children = this._mergeOperatingNumberDataNode(
            srcId,
            srcNode.children,
            target[existingIndex].children,
          );
        }
      }
    }

    return target;
  }
}
